import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import type { CustomEmberTableRow } from '@mylighthouse/prism-ember/types/components/prism/table';
import SettingsBudgetForecastTablesCellsTdEntryTitle from 'lumen/components/settings/budget-forecast/tables/cells/td-entry-title';
import SettingsBudgetForecastTablesCellsTdWithActions, {
  type Action,
  type AdditionalAction,
} from 'lumen/components/settings/budget-forecast/tables/cells/td-with-actions';
import type { BudgetForecastEntry } from 'lumen/models';

type Actions = {
  actions: Action[];
  additionalActions: AdditionalAction[];
};

type ColumnValuePath =
  | 'actions'
  | 'created'
  | 'createdBy'
  | 'lastEdited'
  | 'title';

type Column = {
  displayValuePath?: string;
  isSortable?: boolean;
  name: string;
  tdComponent?:
    | typeof SettingsBudgetForecastTablesCellsTdEntryTitle
    | typeof SettingsBudgetForecastTablesCellsTdWithActions;
  textAlign: 'left' | 'right';
  valuePath: ColumnValuePath;
};

type CompareValue = -1 | 0 | 1;

interface Row extends CustomEmberTableRow {
  actions: Actions;
  createdBy: string;
  displayValue: {
    created: string;
    lastEdited: string;
    title: BudgetForecastEntry;
  };
  isMain: boolean;
  lastEdited: number | null;
  title: string;
}

interface SettingsBudgetForecastTableEntriesSignature {
  Args: {
    entries?: BudgetForecastEntry[];
    isLoading: boolean;
    onArchiveClick?: (entry: BudgetForecastEntry) => void;
    onCopyToExisting: (entry: BudgetForecastEntry) => void;
    onDeleteEntry: (entry: BudgetForecastEntry) => void;
    onDuplicateEntry: (entry: BudgetForecastEntry) => void;
    onDuplicateEntryAsOtherType: (entry: BudgetForecastEntry) => void;
    onEditTitle: (entry: BudgetForecastEntry) => void;
    onLockEntry: (entry: BudgetForecastEntry) => void;
    onRestoreEntry?: (entry: BudgetForecastEntry) => void;
    onUnlockEntry: (entry: BudgetForecastEntry) => void;
    view: 'budget' | 'forecast';
  };
}

export default class SettingsBudgetForecastTableEntries extends Component<SettingsBudgetForecastTableEntriesSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  initialSorts = [
    {
      isAscending: true,
      valuePath: 'title',
    },
  ];

  get columns(): Column[] {
    return [
      {
        displayValuePath: 'displayValue.title',
        name: this.intl.t('budget_forecast.title'),
        tdComponent: SettingsBudgetForecastTablesCellsTdEntryTitle,
        textAlign: 'left',
        valuePath: 'title',
      },
      {
        name: this.intl.t('budget_forecast.created_by'),
        textAlign: 'left',
        valuePath: 'createdBy',
      },
      {
        displayValuePath: 'displayValue.created',
        name: this.intl.t('budget_forecast.created_at'),
        textAlign: 'right',
        valuePath: 'created',
      },
      {
        displayValuePath: 'displayValue.lastEdited',
        name: this.intl.t('budget_forecast.last_edited_at'),
        textAlign: 'right',
        valuePath: 'lastEdited',
      },
      {
        isSortable: false,
        name: '',
        tdComponent: SettingsBudgetForecastTablesCellsTdWithActions,
        textAlign: 'right',
        valuePath: 'actions',
      },
    ];
  }

  get rows(): Row[] {
    const { entries } = this.args;

    if (!entries) {
      return [];
    }

    return entries.map((entry) => {
      const {
        createdAt,
        createdAtFromNow,
        id,
        isMain,
        lastEditedAt,
        lastEditedAtFromNow,
        titleWithDefault,
      } = entry;

      return {
        actions: this.getActionsCellData(entry),
        created: createdAt ? createdAt.unix() : null,
        createdBy: this.getCreatedByDisplayName(entry),
        displayValue: {
          created: createdAtFromNow,
          lastEdited: lastEditedAtFromNow,
          title: entry,
        },
        id,
        isMain,
        lastEdited: lastEditedAt ? lastEditedAt.unix() : null,
        title: titleWithDefault,
      } satisfies Row;
    });
  }

  getActionsCellData(entry: BudgetForecastEntry): Actions {
    const disabledMessage =
      entry.dataType === 'budget'
        ? this.intl.t('budget_forecast.budget.locked_message')
        : this.intl.t('budget_forecast.forecast.locked_message');

    return {
      actions: [
        {
          icon:
            entry.isArchived || entry.isLocked || !entry.canEdit
              ? ('arrow-right' as const)
              : ('edit-pencil' as const),
          actionHandler: () => this.onEditClicked(entry),
          dataTestId: 'edit',
        },
        {
          icon: 'bin' as const,
          actionHandler: () => this.args.onDeleteEntry(entry),
          predicate: !entry.isMain && entry.canEdit,
          disabled: entry.isLocked,
          disabledMessage: disabledMessage,
          dataTestId: 'delete',
        },
      ].filter((action) => action.predicate !== false),
      additionalActions: [
        {
          label: this.intl.t('budget_forecast.edit_title'),
          actionHandler: () => this.args.onEditTitle(entry),
          predicate: !entry.isMain && !entry.isArchived && entry.canEdit,
          disabled: entry.isLocked,
          disabledMessage: disabledMessage,
        },
        {
          label: this.intl.t('budget_forecast.duplicate'),
          actionHandler: () => this.args.onDuplicateEntry(entry),
        },
        {
          label:
            entry.dataType === 'budget'
              ? this.intl.t('budget_forecast.duplicate_as_forecast')
              : this.intl.t('budget_forecast.duplicate_as_budget'),
          actionHandler: () => this.args.onDuplicateEntryAsOtherType(entry),
        },
        {
          label:
            entry.dataType === 'budget'
              ? this.intl.t('budget_forecast.copy_to_existing.budget')
              : this.intl.t('budget_forecast.copy_to_existing.forecast'),
          actionHandler: () => this.args.onCopyToExisting(entry),
        },
        {
          label: this.intl.t('budget_forecast.archive'),
          actionHandler: () => this.args.onArchiveClick!(entry),
          predicate: !entry.isMain && !entry.isArchived && entry.canEdit,
          disabled: entry.isLocked,
          disabledMessage: disabledMessage,
        },
        {
          label: this.intl.t('budget_forecast.restore'),
          actionHandler: () => this.args.onRestoreEntry!(entry),
          predicate: entry.isArchived && entry.canEdit,
        },
        {
          label: this.intl.t('budget_forecast.lock'),
          actionHandler: () => this.args.onLockEntry(entry),
          predicate: !entry.isArchived && !entry.isLocked && entry.canEdit,
        },
        {
          label: this.intl.t('budget_forecast.unlock'),
          actionHandler: () => this.args.onUnlockEntry(entry),
          predicate: entry.isLocked && entry.canEdit,
        },
      ].filter((action) => action.predicate !== false),
    };
  }

  getCreatedByDisplayName(entry: BudgetForecastEntry): string {
    if (!entry.createdBy) {
      return this.intl.t('general.missing');
    }

    const isCreatedByCurrentUser =
      entry.createdBy.id === this.hotelAndMarketService.user.id;

    const { name } = entry.createdBy;
    return isCreatedByCurrentUser
      ? this.intl.t('budget_forecast.created_by_you', { name: name })
      : name;
  }

  @action
  onEditClicked(entry: BudgetForecastEntry): void {
    if (this.args.view === 'budget') {
      this.router.transitionTo('hotel.settings.budget.detail', entry.id);
    } else {
      this.router.transitionTo('hotel.settings.forecast.detail', entry.id);
    }
  }

  @action sortFunction(
    rowA: Row,
    rowB: Row,
    sorts: {
      isAscending: boolean;
      valuePath: ColumnValuePath;
    }[],
    compare: (
      valueA: unknown,
      valueB: unknown,
      sortEmptyLast: boolean,
    ) => CompareValue,
    sortEmptyLast: boolean,
  ): CompareValue {
    if (rowA.isMain) {
      return -1;
    }

    if (rowB.isMain) {
      return 1;
    }

    for (let i = 0; i < sorts.length; i++) {
      const { isAscending, valuePath } = sorts[i]!;

      const valueA = rowA[valuePath];
      const valueB = rowB[valuePath];
      const sign = isAscending ? 1 : -1;

      const compareValue = (sign *
        compare(valueA, valueB, sortEmptyLast)) as CompareValue;

      if (compareValue !== 0) {
        return compareValue;
      }
    }

    return 0;
  }

  <template>
    <PrismTable
      @columns={{this.columns}}
      @enableAutoSortsUpdate={{true}}
      @initialSorts={{this.initialSorts}}
      @isLoading={{@isLoading}}
      @removeTableBorder={{true}}
      @rows={{this.rows}}
      {{! @glint-expect-error: Type 'T' is not assignable to type 'Row' }}
      @sortFunction={{this.sortFunction}}
      data-test-table="budget-forecast-entries"
    />
  </template>
}
